import type { AnalyticsProvider, AnalyticsEventName, AnalyticsPayload } from "../types"
import { getEffectiveParam } from "@/features/config"
import { getDeviceType } from "@/shared/lib/native-browser"
import {
  drainGtagQueue,
  enqueueGtagEvent,
  hasTrackingConsent,
  isTrackingDeclined,
} from "../cookie-consent"

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

/**
 * Resolved at init time: the funnel config's `gg` param (priority 0) wins over
 * the build-time env id, so a config can retarget GA without a redeploy.
 */
let gaId = ""
/** gtag is configured and the script tag has been requested. */
let ready = false
/** Consent was granted, but there is no measurement id — drop events. */
let skipped = false

/**
 * Explicit mapping of the analytics payload to GA4 event params. Only keys that
 * actually have a value are emitted — plain screen events (e.g.
 * `Simplified_Quiz_Loaded`) carry just `timestamp`/`device_type` (+ campaign
 * when attributed); the ecommerce fields appear only on checkout/purchase
 * events that pass them. Nothing empty is sent.
 */
function toGaEventParams(payload: AnalyticsPayload): Record<string, unknown> {
  const params: Record<string, unknown> = {
    // Globals on every event (analytics brief): when, what device, which campaign.
    // `?? undefined` normalizes null attribution so it gets stripped below.
    timestamp: Date.now(),
    device_type: getDeviceType(),
    campaign_id: payload.attribution?.campaign_id ?? undefined,
    campaign_name: payload.attribution?.campaign_name ?? undefined,
    // Ecommerce fields — present only on events that pass them (begin_checkout,
    // purchase, ...). GA4 ecommerce expects `value` + `transaction_id` on purchase.
    product_id: payload.productId,
    currency: payload.currency,
    payment_method: payload.paymentMethod,
    value: payload.price,
    transaction_id: payload.transactionId,
    // Event-specific extras passed by the caller.
    ...payload.metadata,
  }

  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  )
}

function emit(event: string, params?: Record<string, unknown>) {
  if (isTrackingDeclined() || skipped) return
  if (ready) {
    window.gtag("event", event, params)
    return
  }
  enqueueGtagEvent(event, params)
}

export const gaProvider: AnalyticsProvider = {
  name: "google-analytics",

  init() {
    if (ready || skipped || !hasTrackingConsent()) return

    gaId = getEffectiveParam("gg")?.trim() || import.meta.env.VITE_GA_MEASUREMENT_ID || ""
    if (!gaId) {
      skipped = true
      drainGtagQueue()
      return
    }

    const script = document.createElement("script")
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
    script.async = true
    document.head.appendChild(script)

    window.dataLayer = window.dataLayer || []
    // gtag.js only processes `arguments` objects pushed to the dataLayer, not
    // plain arrays — pushing an array silently no-ops, so config/events never
    // reach Google. Use Google's exact snippet form.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments)
    }
    window.gtag("js", new Date())
    window.gtag("config", gaId)
    ready = true

    for (const item of drainGtagQueue()) {
      window.gtag("event", item.event, item.params)
    }
  },

  track(event: AnalyticsEventName, payload: AnalyticsPayload) {
    emit(event, toGaEventParams(payload))
  },

  page(path: string) {
    emit("page_view", { page_path: path })
  },
}
